// F2/F3/F6 — **THE CAPTURE, THE READING'S SHAPE, AND THE BRIDGE.**
//
// Her order: EMAIL REQUIRED, PHONE OPTIONAL, with the consent line, plus the birth details the
// reading needs — birth date required, time and place per what the reading uses, **and the TIMELESS
// LAW applies when the time is unknown.** The reading is a REAL reading off real birth data through
// the house's real engines, with a card pull woven in, in her register, ending on the double hook.
//
// **IT IS A LAW MODULE AND NOT A VALIDATOR AT THE ROUTE, FOR THE REASON EVERY OTHER LAW HERE IS
// ONE:** the form she fills in and the door that accepts it must never be two answers to what a
// complete submission is (R15 — a picker that offers what the gate refuses costs the whole entry
// before she is told no, and this is a stranger the owner PAID to bring here, who does not try
// twice). Shared `.js` rather than `src/lib/*.ts`, because the SERVER is the side that enforces it.
//
// It is beside `src/funnels.js` rather than inside it: that module is the three DOORS and is read by
// the ad URLs, the page, the lead's source column and F5's buckets; this is what a SUBMISSION is.
// Two files, because a fourth funnel and a new form field are different edits (R28/R29 cuts the
// other way when two things merely happen to be near each other).
import { funnelFromParam } from "./funnels.js";
// C8b — the ONE answer to *what is a dialable number*, read by this capture and by the member path.
import { normalizePhone } from "./memberPhone.js";

/** Her consent line. It rides the SMS audience per the standing rail, and it says what it is FOR —
 *  a checkbox whose sentence does not name the thing consented to is not consent.
 *
 *  **HER TRIM, 2026-08-13: THE UNSUBSCRIBE PROMISE LEAVES THE FORM AND STAYS IN THE FOOTER.** It is
 *  not deleted from the house — `broadcastEmailHtml` cannot compose a body without an unsubscribe URL
 *  and `List-Unsubscribe` rides every send — so the promise is kept where it is ACTIONABLE. On the
 *  form it was a sentence about leaving printed on the one control that asks her to arrive. */
export const FUNNEL_CONSENT =
  "Yes, send me my reading and messages from 12th House.";

/**
 * **HER RULING, 2026-08-13: THE BIRTH TIME AND THE BIRTHPLACE ARE NOT OPTIONAL.**
 *
 * They were, and the reason they were is still true — every required field is a lead lost on a phone
 * at a bus stop. What changed is what they COST. A reading cast with no place has no coordinates at
 * all, and a reading cast with no time has no houses; her own accuracy clause is that these read like
 * she read THEM, and two of the three doors are about timing and money, which are the house-derived
 * half. **So the field is required and the OUT is explicit rather than silent:** birth time carries an
 * UNKNOWN box which accepts the lead and takes the timeless path, said in one warm line before she
 * submits. That is the difference between a stranger who chose the timeless reading and one who left
 * a box empty and never found out what it cost her.
 *
 * **THE BIRTHPLACE REQUIRES THE TEXT AND NOT THE COORDINATES**, deliberately (DECISIONS FUNNEL-QC-4):
 * the picker resolves them before submit and `ensureChartPlace` is the fallback, so demanding a
 * resolved row would refuse a real visitor whose town the geocoder does not list — which is R19's
 * expensive direction on a door the owner PAID to bring her to.
 *
 * Declared as rosters so the form and the door read one answer.
 */
export const FUNNEL_REQUIRED = ["email", "birthDate", "birthTime", "birthPlace", "consent"];
export const FUNNEL_OPTIONAL = ["phone"];

// A deliberately forgiving address test. The strict thing is to refuse anything unusual; the
// EXPENSIVE thing is refusing a real address she paid to acquire, so this asks only for the shape a
// mail rail can actually send to (R19: the two directions are not the same size).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// ISO, because the field is a native date input. Somebody typing a date freehand is not a case this
// page has, and inventing a parser for one would be a second date convention in this house.
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * **IS THIS SUBMISSION COMPLETE? ONE ANSWER, READ BY THE FORM AND BY THE DOOR.**
 *
 * Returns violation KEYS in the order they are asked, so a surface answers the first one and never
 * has to know the precedence — `messageGateViolations`' own shape, one room over.
 *
 * **`honeypot` IS F6's CHEAPEST FENCE AND IT IS ASKED FIRST.** A field no human can see, filled in
 * only by something reading the DOM. It costs a bot nothing to defeat once somebody notices, which
 * is exactly why it is the FIRST of several rather than the only one — the throttle and the unique
 * index behind it are what actually hold.
 */
export function leadViolations(input = {}) {
  const out = [];
  if (String(input?.honeypot ?? "").trim()) out.push("bot");
  const email = String(input?.email ?? "").trim();
  if (!email) out.push("email-missing");
  else if (!EMAIL_RE.test(email) || email.length > 254) out.push("email-shape");
  if (!input?.consent) out.push("consent");
  const date = String(input?.birthDate ?? "").trim();
  if (!date) out.push("birth-date-missing");
  else if (!DATE_RE.test(date) || Number.isNaN(Date.parse(`${date}T00:00:00Z`))) out.push("birth-date-shape");
  // A phone is OPTIONAL, so an absent one is not a violation — but a phone that cannot be dialled is
  // a lead she will try to reach and cannot, which is a different fact from no phone at all (R18).
  //
  // **C8b — THIS DELEGATES NOW, AND THE DELEGATION IS THE POINT OF THAT ROUND RATHER THAN A TIDY-UP.**
  // The member path needs the same answer, and *what is a dialable number* written twice is two
  // answers (R28/R29) — with the copy that drifts deciding whether her SMS audience can be reached at
  // all. `normalizePhone`'s `empty` outcome is exactly this rule's *an absent one is not a violation*,
  // so the shape of the law here did not change: only the answer key moved to where both sides read
  // it. (It is STRICTER than the seven-digit count it replaces, which accepted `1234567` as a lead
  // this house could ring.)
  if (!normalizePhone(input?.phone).ok && normalizePhone(input?.phone).why !== "empty") out.push("phone-shape");
  // **HER RULING: THE TIME IS REQUIRED, AND `UNKNOWN` IS THE ANSWER RATHER THAN THE ABSENCE.** Three
  // facts, not two (R18): a readable time, a ticked box, and a field left empty by somebody who has
  // not decided. Only the third is a violation — and it is the one that used to pass silently and
  // cost her the houses.
  //
  // **THE BOX OUTRANKS THE FIELD**, so a visitor who typed a time and then ticked Unknown is taken at
  // her word rather than told her own tick is a contradiction. `funnelClock` is asked rather than a
  // non-empty test, because a time this house cannot read is a time that would cast at noon or refuse
  // at the vendor with nothing on screen to say so — the defect this round traced.
  if (!input?.birthTimeUnknown) {
    const time = String(input?.birthTime ?? "").trim();
    if (!time) out.push("birth-time-missing");
    else if (!funnelClock(time)) out.push("birth-time-shape");
  }
  if (!String(input?.birthPlace ?? "").trim()) out.push("birth-place-missing");
  return out;
}

/**
 * One sentence per refusal, in the house's voice.
 *
 * **A BOT IS TOLD NOTHING DIFFERENT FROM A PERSON WITH A TYPO.** An automated caller that learns
 * which field it tripped is an automated caller somebody has taught — LR1 ruling 2's shape on a
 * public door, where the refusal must not describe the fence.
 */
export const LEAD_REFUSALS = {
  bot: "That did not go through. Try again.",
  "email-missing": "I need your email to send your reading.",
  "email-shape": "That email does not look right. Check it and try again.",
  consent: "Tick the box so I can send it to you.",
  "birth-date-missing": "I need your birth date to read your chart.",
  "birth-date-shape": "That birth date does not look right.",
  "birth-time-missing": "I need your birth time. If you do not know it, tick the box and I will read you without it.",
  "birth-time-shape": "That birth time does not look right.",
  "birth-place-missing": "I need where you were born. Start typing and pick your town from the list.",
  "phone-shape": "That phone number does not look right. Leave it blank if you would rather not.",
};
export const leadRefusal = (hits = []) => LEAD_REFUSALS[hits[0]] || LEAD_REFUSALS.bot;

/**
 * **WHAT EACH DOOR'S READING IS, AND WHICH HOUSES ANSWER ITS QUESTION.**
 *
 * >>> **L5 (her live-test order, 2026-08-13) — THE CARD PULL IS DROPPED ENTIRELY.** <<< Her ruling
 * after her daughter walked the live funnel: *"the funnel readings drop the card pull entirely.
 * Astrology only: aspects, synastry, planetary positions, and the houses that answer the funnel's
 * question (for wealth: the 2nd, the 10th, Jupiter's house, the Sun's house, where they SHINE). The
 * card engine stays out of the funnel."*
 *
 * **`spread` AND `cards` ARE DELETED AT THEIR DECLARATION** (DL-2/DT-2b/R181) rather than left at
 * zero or ignored by the caller: a spread name sitting in this table is one parameter away from a
 * round that finds it and wires the deck back in, and every instrument that quoted them is migrated
 * in the same push.
 *
 * **`houses` IS WHAT REPLACES THEM, AND IT IS A PROPERTY OF THE DOOR RATHER THAN A LIST TYPED AT THE
 * GENERATOR** (R11/R31): her wealth door names the 2nd, the 10th, Jupiter's house and the Sun's
 * house by name, and a fourth door joins by being named here. **It is a lens on the material, never
 * a vocabulary for the text** — L6 forbids the reading from naming a house out loud, so this decides
 * what the model READS and never what it SAYS.
 *
 * **`focus` IS WHAT KEEPS THREE READINGS OFF ONE GENERIC HOROSCOPE** — it is what her *"it reads
 * like she read THEM"* clause is really about, and it is why the chart grounding below is asked for
 * a subject rather than dumped whole.
 */
export const FUNNEL_READINGS = {
  wealthy: {
    kicker: "Your Money Chart",
    focus: "their money story: what they were built to earn, hold and lose, where they SHINE, and "
      + "the shape of the thing that keeps getting in the way of it",
    houses: [2, 10],
    lens: "the 2nd house (what they earn and hold), the 10th (what they are known for), Jupiter's "
      + "house and sign (where it comes easy), and the Sun's house and sign (where they SHINE)",
  },
  psychic: {
    kicker: "What You Were Given",
    focus: "what they receive and how: the channel they were given, the way it comes through, and "
      + "the thing they keep explaining away as coincidence",
    houses: [8, 12],
    lens: "the 12th house (what comes through unbidden), the 8th (what they see that others do "
      + "not), the Moon's house and sign, and Neptune's",
  },
  timeline: {
    kicker: "Your Wealth Timeline",
    focus: "the seasons of their money: what has already turned, what is turning now, and what is "
      + "coming that they can get ready for",
    houses: [2, 10],
    lens: "the 2nd and 10th houses, Saturn's house and sign (what has been paid for already) and "
      + "Jupiter's (what opens next)",
  },
};

/**
 * **THE DOUBLE HOOK — HER OWN EXAMPLES, PER THE FUNNEL'S THEME.**
 *
 * The reading ends on a warm line naming the NEXT reading, and the button carries them to the home
 * page with the tier popup ready. **The line names a reading this house really delivers**, because a
 * bridge promising something nobody can buy is the funnel's own defect one screen later.
 */
export const FUNNEL_BRIDGE = {
  wealthy: {
    line: "Want to see what is actually blocking your money?",
    next: "What's Blocking Your Money",
    // **HER REDESIGN'S `decodes` — THE NOUN THIS DOOR IS ABOUT, IN HER OWN WORDS.** It is a property
    // of the funnel rather than a phrase typed into the framing sentence, because the framing is ONE
    // sentence read by the page and the email and a fourth door joins by being named here (R11/R31).
    decodes: "your wealth",
  },
  psychic: {
    line: "Want to see how your partner really feels about you?",
    next: "What Your Partner Thinks of You",
    decodes: "your psychic ability",
  },
  timeline: {
    line: "Want to see what is actually blocking your money?",
    next: "What's Blocking Your Money",
    decodes: "your timeline",
  },
};

/**
 * ================================================================================================
 * **HER REDESIGN, 2026-08-13 — THE FINISH DOOR. THE READING IS NOT THE PRIZE, FINISHING IT IS.**
 *
 * The full reading is generated once and STORED against the lead's row. It is never shown whole on
 * this page and never emailed. What a stranger reads is ONE paragraph that cliffhangs, and the only
 * way to the rest of it is through the door.
 *
 * **THE CTA IS `Finish this reading` AND NOT `Open my full reading`**, which is not a re-wording: the
 * retired line promised something the page could deliver by scrolling, and this one names an act only
 * the house can complete. The button lands on the SAME signed handoff the continuity law already
 * carries, so nothing about the bridge's security changes — only what it says it is for.
 *
 * **THE FRAMING IS COMPOSED FROM THE FUNNEL'S OWN `decodes`**, so the sentence a stranger reads names
 * the thing she came for rather than a generic membership pitch, and the roster below carries the
 * other hooks — her clause: *"plus the other hooks."* **The hooks are the ones this house really
 * delivers**, because a bridge promising a reading nobody can buy is the funnel's own defect one
 * screen later (`FUNNEL_BRIDGE`'s own standing ruling, applied to the roster).
 * ================================================================================================
 */
export const FUNNEL_FINISH_CTA = "Finish this reading";
/**
 * **L9 (her live-test order, 2026-08-13) — THE HOOKS RENDER IN TITLE CASE.** Her words: *"the teaser
 * bullets render in Title Case, properly aligned with their bullets (not lowercase, not
 * centered-text-with-left-bullets)."* The CASE is in the data rather than in a `text-transform`,
 * because Title Case is not a mechanical transform — a stylesheet can upper-case a line and cannot
 * tell *"what is blocking your money"* from *"What Is Blocking Your Money"*, which is what she asked
 * for. The ALIGNMENT is the sheet's, where alignment belongs.
 */
export const FUNNEL_OTHER_HOOKS = [
  "How Someone Really Feels About You",
  "What Is Blocking Your Money",
  "What You Were Given, And How It Comes Through",
];
/**
 * **L9 — THE WORDS "THE HOUSE" ARE GONE, ON HER OWN REASONING: STRANGERS DO NOT KNOW WHAT THE HOUSE
 * IS.** Her line, verbatim: *"TO FINISH THIS READING, JOIN AND UNLOCK EVERYTHING ELSE THAT DECODES
 * YOUR WEALTH."* It is written in sentence case here and UPPER-CASED BY THE SHEET, which is where
 * that rule already lives for this element — one answer to how the line is drawn, and the roster
 * stays readable to anybody composing from it (the email reads this same function).
 */
export function funnelFinishFraming(key) {
  const b = funnelBridge(key);
  return `To finish this reading, join and unlock everything else that decodes ${b.decodes}.`;
}
/**
 * **E4 (owner, 2026-08-13) — THE LINE THAT FRAMES THE MAIL, IN HER REGISTER.**
 *
 * Her clause for the funnel email is *"the header, a line in her register framing it (`your reading
 * is ready` energy), the paragraph or its tease, and the FINISH THIS READING gold button."* E3 gave
 * this mail the shell, the header and the button; **it still opened cold, straight into the
 * paragraph with no heading at all** — which reads as a forwarded fragment rather than as something
 * sent to her.
 *
 * **IT IS DERIVED FROM THE FUNNEL'S OWN `decodes` RATHER THAN TYPED PER DOOR (R11/R31)**, so the
 * line a stranger reads names the thing she came for — and a fourth funnel gets a correct lede by
 * EXISTING rather than by somebody remembering this file. `decodes` already reads *"your wealth"*,
 * so the possessive is the funnel's and this sentence adds none of its own.
 *
 * **REGISTER (N5/N12-3): SAY THE REAL THING FIRST, NO WINDUP.** It states what arrived and stops.
 * It carries no em dash (this house writes none in either language), makes no promise the mail does
 * not keep, and does not greet — a stranger off an advertisement has not told us her name, and a
 * mail that opens *"Hi there!"* is the AI-flavoured warmth the voice gate exists to refuse.
 */
export function funnelEmailLede(key) {
  const b = funnelBridge(key);
  return `Your reading on ${b.decodes} is ready.`;
}

/** Kept as the name every caller already asks for; it now answers the finish door's word (R165: what
 *  changes is the ANSWER, not the name a reader looks for). */
export const FUNNEL_BRIDGE_CTA = FUNNEL_FINISH_CTA;
export const funnelBridge = (key) => FUNNEL_BRIDGE[funnelFromParam(key)];
export const funnelReading = (key) => FUNNEL_READINGS[funnelFromParam(key)];

/**
 * **WHERE THE BRIDGE LANDS: THE HOME PAGE, WITH THE TIER POPUP READY** — her clause, literally. The
 * handoff token rides the LINK, so the continuity law is a property of the address rather than of
 * anything the client remembered, and the server never takes the client's word for whose data this
 * is (her continuity clause, in as many words).
 */
export const FUNNEL_BRIDGE_PATH = "/";
export const bridgeHref = (token) =>
  `${FUNNEL_BRIDGE_PATH}?join=1${token ? `&lead=${encodeURIComponent(token)}` : ""}`;

/**
 * **THE TIMELESS LAW, ASKED HERE SO ONE ANSWER SERVES THE FORM AND THE ENGINE.**
 *
 * No birth time means planets in signs and **nothing house-derived, ever guessed.** This does not
 * re-implement it — `src/timelessChart.js` is the law and `birthTimeUnknown` already casts at noon —
 * it names WHICH READING a submission gets, so the form can say so BEFORE she submits rather than
 * after. **A place with no time still gets no houses:** houses need the minute, not the city.
 */
export const leadIsTimeless = (input = {}) =>
  Boolean(input?.birthTimeUnknown) || !funnelClock(input?.birthTime);
export const TIMELESS_NOTE =
  "No birth time? That is fine. I will read your planets by sign and leave your houses alone rather "
  + "than guess at them.";

/**
 * ================================================================================================
 * **THE CLOCKS DISAGREE, AND IT COST HER OWN SUBMISSION ITS READING (traced 2026-08-13).**
 *
 * The funnel's field is `<input type="time">`, which is a **24-hour** value (`"00:30"`, `"13:45"`).
 * The house's front door is a **12-HOUR** field beside an AM/PM select, and `convertClientBirthTime`
 * on the server reads a chart that way. Handing the funnel's string across unchanged does not fail —
 * **it CASTS**:
 *
 *   · `"13:45"` lands as 1:45 **AM**, a chart twelve hours out, and nothing anywhere is red.
 *   · `"00:30"` lands as hour 0, and that guard is `if (!rawHour)` — **zero is falsy** — so midnight
 *     resolves to `null`, the payload is refused, and a visitor born just after midnight gets no
 *     reading at all, silently.
 *
 * **THIS LAW LIVES HERE, IN THE SHARED `.js`, RATHER THAN IN `src/lib/funnel-prefill.ts` WHERE IT WAS
 * WRITTEN — AND THE MOVE IS THE POINT (R28/R29).** It was a CLIENT law about the pre-fill, and the
 * SERVER composes the cast from the same string. Two readers of *what o'clock this is* is exactly how
 * one of them comes to be right and the other to cast twelve hours out; `src/lib/*.ts` is client-only,
 * so the server could not have read it there. `funnel-prefill.ts` imports it from here now
 * (`avatar-photo.ts`/`pwa.ts`/`support-read.ts`'s own shape — a `src/lib` module may read a shared
 * `.js` law, because a `.mjs` gate can still load the chain).
 *
 * Four cases, and the two that matter are the ones a naive `h > 12 ? h - 12 : h` gets wrong:
 *
 *   00:xx -> 12:xx AM   (midnight is TWELVE AM, never zero — and zero is what the server refuses)
 *   01:xx -> 01:xx AM
 *   12:xx -> 12:xx PM   (noon is TWELVE PM, never zero PM)
 *   13:xx -> 01:xx PM
 *
 * The minute is padded, because the front door's own field is matched `^(\d{1,2}):(\d{1,2})$` and a
 * bare `"9:5"` is nine minutes past nine to a reader and five past nine to that regex.
 *
 * **AN UNREADABLE TIME ANSWERS `null` RATHER THAN A GUESS**, and every caller turns that into the
 * timeless path — the safe direction: a reading with no houses is honest, a reading with houses cast
 * off a guessed minute is confidently wrong (R19).
 *
 * The answer key is the house's own 12-to-24 reader (`convertToTwentyFourHour`), held by the fixture
 * as a **BICONDITIONAL over all 1,440 minutes of the day**: what this writes, that must read back as
 * the exact minute the visitor typed. Nothing anywhere re-implements the arithmetic, so there is no
 * second answer to compare against.
 * ================================================================================================
 */
export function funnelClock(value) {
  const clock = /^(\d{1,2}):(\d{1,2})$/.exec(String(value || "").trim());
  if (!clock) return null;
  const hour24 = Number(clock[1]);
  const minute = Number(clock[2]);
  if (!Number.isInteger(hour24) || !Number.isInteger(minute)) return null;
  if (hour24 < 0 || hour24 > 23 || minute < 0 || minute > 59) return null;
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { birthTime: `${hour12}:${String(minute).padStart(2, "0")}`, birthPeriod: period };
}

/**
 * **THE ONE SENTENCE A LEAD WHOSE READING DID NOT COME BACK IS TOLD — AND IT IS TRUE BY THE RAIL
 * UNDER IT, NOT BY THE WORDING (her order, 2026-08-13).**
 *
 * What shipped was *"Your reading did not come through. I have your details and I will send it."* —
 * her own words for it: confusing to a stranger, and it named a failure a stranger has no way to act
 * on. Worse, **the second half was a promise nobody kept**: there was no retry and no sender, so the
 * row sat with a NULL reading forever and the sentence was a dead promise on the one screen this
 * house asks a stranger to trust.
 *
 * **THE SENTENCE IS NOT THE FIX AND MUST NEVER BE SHIPPED WITHOUT THE RAIL** (`retryLeadReading` and
 * the sweeper): a warmer dead promise is worse than a cold one. No machinery talk, nothing about
 * charts, engines, vendors or errors, because none of it is hers to hold.
 *
 * **IT SENDS HER BACK TO THE PAGE RATHER THAN TO HER INBOX (her redesign, clause 6).** Under the
 * stored-reading model the email no longer carries a reading at all, so *"it will arrive in your
 * email"* would be the dead promise again wearing the new architecture. The page is where the
 * paragraph appears, and a repeat submission from the same address hands back what the retry stored.
 */
export const LEAD_READING_PENDING =
  "Your reading is being finished right now. Come back in a moment and it will be here.";

/**
 * ================================================================================================
 * **A ZONE THAT IS NOT A NUMBER IS NOT AN OFFSET — AND THIS IS THE STEP THAT REFUSED HER READING.**
 *
 * Traced 2026-08-13 off her own row: `timezone: "America/New_York"`, coordinates perfect, reading
 * NULL, `gate_log` twice with `requiresInput`. The picker writes the geocoder's `timezone_id`, which
 * is an IANA **NAME**; `buildAstrologyPayload` wants a numeric UTC **OFFSET**; and
 * `ensureChartTimezone` reads a non-empty `timezone` as *already provided* and returns without
 * resolving one. So `verifiedNumber("America/New_York")` is `NaN`, the payload is `null`, and the
 * cast answers `requiresInput` — **for every visitor who used the picker**, which is every visitor
 * since the precision law shipped. Before the picker the field was empty and the rung resolved it,
 * which is why `funnel-lead-walk.mjs` was 27/27 green over this: it posts a free-text place.
 *
 * The house's own form has kept these apart forever — `resolvedPlacePatch` writes `timezoneName` for
 * the name and leaves `timezone` for the offset — and `ensureChartPlace`'s own comment says why the
 * name is not merely a different spelling: *"Geoapify's `timezone_id` is an IANA NAME and
 * `buildAstrologyPayload` wants a numeric OFFSET for that DATE — a 1982 birth in Toronto is not
 * today's offset."* **Resolving from the coordinates and the birth date is not a workaround, it is
 * the more correct answer**, and it is what the rung below already does.
 *
 * **THREE OUTCOMES, NOT TWO (R18), AND THE CLASSIFIER IS WHY OLD ROWS HEAL.** A row captured before
 * this repair carries a NAME in the offset column, and every one of them is owed a reading — so the
 * value is asked what KIND it is rather than trusted to be an offset. An empty zone is neither: it is
 * the honest *nobody told us*, which the rung resolves.
 *
 * A genuine `"0"` is an OFFSET (UTC), which is `verifiedNumber`'s own hard-won case: `Number("")` is
 * 0 and that is exactly how an unresolved zone once cast a whole house at the equator.
 * ================================================================================================
 */
export function funnelZone(value) {
  const raw = value === null || value === undefined ? "" : String(value).trim();
  if (raw === "") return {};
  const n = Number(raw);
  return Number.isFinite(n) ? { offset: raw } : { name: raw };
}

/**
 * ================================================================================================
 * **L11 — WHAT THE STORED READING BECOMES ON HER OWN SHELF (her order, 2026-08-13).**
 *
 * Her clause: *"On completion the FULL stored reading lands in their new room. The full reading
 * appears in their room, and tapping it as a free account fires the subscription popup."*
 *
 * The row holds `{ funnel, kicker, timeless, overview, sections, bridge }` — the shape
 * `buildFunnelReading` returns — and `ReportBody` renders a saved reading through
 * `NarrativeReportView`, whose shape is `{ type, header, overview, sections }`. **This is the one
 * declaration that crosses between them**, and it lives in the shared `.js` for `strings.js`' own
 * reason: the SERVER composes this row and `src/lib/*.ts` is CLIENT-ONLY, so a converter written
 * there could not be called by the thing that calls it, and one written inside `server/` could not
 * be RUN by a FAST gate.
 *
 * **THE BRIDGE IS DROPPED, AND THAT IS THE WHOLE POINT OF CONVERTING RATHER THAN STORING A VIEW.**
 * `reading.bridge` is *"To finish this reading, join and unlock everything else…"* — a sentence
 * addressed to a STRANGER, and she is a member now. Left in, the first reading a brand-new member
 * ever opens ends by asking her to do the thing she has just done (R68's vanished-anchor class one
 * layer up: a promise that no longer applies reads as a promise that was never kept).
 *
 * **NO NAME IS INVENTED AND NO EMPTY FIELD IS LABELLED (W12-5).** The funnel never asked for a name,
 * so the header takes the MEMBER'S OWN — she has one by the time this row is written, and it is the
 * same person — and `dateText`/`place` are passed through exactly as they were captured, because
 * `NarrativeReportView` draws those two lines only when they carry something. A blank labelled field
 * reads as a reading that failed to load rather than as a field that does not apply, which is the
 * live defect the Cauldron capture found.
 *
 * **THE TIMELESS FACT TRAVELS (R18).** A visitor who left the time blank was cast timeless and told
 * so; the row on her shelf says the same thing rather than showing an hour nobody gave.
 * ================================================================================================
 */
export function funnelRoomReport(reading, member = {}) {
  const r = reading && typeof reading === "object" ? reading : {};
  const sections = Array.isArray(r.sections) ? r.sections : [];
  return {
    type: FUNNEL_REPORT_TYPE,
    funnel: String(r.funnel || ""),
    kicker: String(r.kicker || ""),
    timeless: r.timeless === true,
    header: {
      name: String(member.name || ""),
      dateText: String(member.dateText || ""),
      // The clock is the member's, and an unknown one is an ABSENCE rather than a guessed hour.
      timeText: r.timeless === true ? "" : String(member.timeText || ""),
      place: String(member.place || ""),
    },
    overview: String(r.overview || ""),
    sections: sections.map((s) => ({
      title: String(s?.title || ""),
      body: String(s?.body || ""),
    })),
  };
}

/**
 * **THE TYPE THE ROW IS FILED UNDER, DECLARED ONCE.** The server writes it, `ROOM_FOR_TYPE` files it,
 * `REPORT_TYPE_LABELS` names it and `ReportBody` renders it — four rosters, and a string typed four
 * times is four chances for one of them to be spelled differently and for a reading to land on a
 * shelf that opens to nothing (R28/R29/R31).
 */
export const FUNNEL_REPORT_TYPE = "funnel-reading";

/**
 * **IS THIS ROW THE BAIT, OR IS IT ONE OF THE READINGS T1-5 GIVES HER FOR FREE?**
 *
 * The gate in `handleReports` asks this and nothing else, so the exception to T1-5 is exactly one
 * type wide and is asked in one place. **A row with no type is NOT the funnel reading** — failing
 * open here costs a stranger's teaser and failing closed walls a member's own birth chart, and only
 * the second one is a member writing in about a reading she paid for (R19).
 */
export const isFunnelReport = (type) => String(type || "") === FUNNEL_REPORT_TYPE;
